import React from 'react'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import toNumber from 'lodash/toNumber'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import DryVanIcon from 'transfix-icons/build/DryVanIcon'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import RadiusIcon from 'transfix-icons/build/RadiusIcon'
import SearchIcon from 'transfix-icons/build/SearchIcon'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { Box, createStyles, Divider, Hidden, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useDebouncedCallback } from 'use-debounce/lib'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { EQUIPMENT_OPTIONS, RADIUS_OPTIONS } from 'apps/fleet-planner/constants/marketplace'
import { useBookableShipmentSavedSearchContext } from 'apps/fleet-planner/context/BookableShipmentSavedSearchContext'
import { useLaneFiltersContext } from 'apps/fleet-planner/context/LaneFiltersContext'
import SavedLaneSwitch from '../../saved-lanes/SavedLaneSwitch'
import { DropoffDatesFilter } from './DropoffDatesFilter'
import { DropoffLocationFilter } from './DropoffLocationFilter'
import { PickupDatesFilter } from './PickupDatesFilter'
import { PickupLocationFilter } from './PickupLocationFilter'

const useFiltersContainerStyles = makeStyles(theme => {
  return createStyles<
    'root' | 'horizantalDivder' | 'verticalDivider' | 'container' | 'searchButton',
    { isEditMode: boolean }
  >({
    root: {
      backgroundColor: brandColors.white,
      display: 'flex',
      border: `1px solid ${brandColors.coolGray4}`,
      padding: 4,
      borderRadius: 8,
      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
      position: 'relative',
      justifyContent: 'space-between',

      [theme.breakpoints.down('md')]: {
        display: 'inline',
      },
    },
    verticalDivider: {
      border: `1px solid ${brandColors.coolGray1}`,
      height: '90%',

      [theme.breakpoints.down('md')]: {
        height: '70%',
      },
    },
    horizantalDivder: {
      marginLeft: -8,
      marginRight: -8,
      border: `1px solid ${brandColors.coolGray4}`,
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),

      [theme.breakpoints.up('md')]: {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(3),
        padding: theme.spacing(3),
      },
    },
    searchButton: {
      minWidth: theme.spacing(5.25),
      padding: theme.spacing(1),
    },
  })
})

export const FiltersContainer = () => {
  const {
    changeParams,
    clearAllFilters,
    createToggleSelectedFilterHandler,
    hasStartedSearch,
    params,
    selectedFilter,
    setHasStartedSearch,
    setSelectedFilter,
  } = useLaneFiltersContext()
  const { refetchBookableShipmentSavedSearches } = useBookableShipmentSavedSearchContext()
  const { isMobile } = useBreakpointContext()
  const { filters } = params || {}
  const { pickupCity, pickupStateCode } = filters || {}

  const showSavedLaneSwitch = hasStartedSearch && pickupCity && pickupStateCode

  const pickupRadiusChangeHandler = (radius?: ISelectOption) => {
    const { value } = radius || {}
    changeParams({
      filters: {
        ...filters,
        pickupMiles: value ? toNumber(value) : undefined,
        pickupTimes: undefined,
      },
    })
  }

  const selectedPickupRadiusOption =
    RADIUS_OPTIONS.filter(option => option.value === params?.filters?.pickupMiles)[0] || ''

  const deliveryRadiusChangeHandler = (radius?: ISelectOption) => {
    const { value } = radius || {}
    changeParams({
      filters: {
        ...filters,
        deliveryMiles: value ? toNumber(value) : undefined,
        pickupTimes: undefined,
      },
    })
  }

  const selectedDeliveryRadiusOption =
    RADIUS_OPTIONS.filter(option => option.value === params?.filters?.deliveryMiles)[0] || ''

  // TODO: Update BE API to take in an array of trailerTypes
  const equipmentChangeHandler = (equipment?: ISelectOption) => {
    const { value } = equipment || {}
    changeParams({
      filters: {
        ...filters,
        trailerType: value ? value.toString() : undefined,
        pickupTimes: undefined,
      },
    })
  }

  const selectedEquipmentOption =
    EQUIPMENT_OPTIONS.filter(option => option.value === params?.filters?.trailerType)[0] || ''

  const startSearchHandler = () => {
    setHasStartedSearch(true)
  }

  const clearFilterHandler = async () => {
    clearAllFilters()
    // Refetch saved searches to update view count
    await refetchBookableShipmentSavedSearches()
  }

  const isEditMode = [
    'pickupStateCode',
    'deliveryStateCode',
    'pickupStartDate',
    'deliveryStartDate',
  ].includes(selectedFilter || '')
  const classes = useFiltersContainerStyles({ isEditMode })

  const pickupStateCodeOpenHandler = () => setSelectedFilter('pickupStateCode')
  const deliveryStateCodeOpenHandler = () => setSelectedFilter('deliveryStateCode')
  const pickupStartDateOpenHandler = () => setSelectedFilter('pickupStartDate')
  const deliveryStartDateOpenHandler = () => setSelectedFilter('deliveryStartDate')

  const [closeHandler] = useDebouncedCallback(() => {
    setSelectedFilter(undefined)
  }, 100)

  return (
    <Box className={classes.container}>
      {isMobile ? (
        /**
         * Mobile view
         */
        <>
          <Box className={classes.root} data-intercom='fms-book-loads-search-bar'>
            <Box>
              <PickupLocationFilter
                onOpen={pickupStateCodeOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'pickupStateCode'}
              />
            </Box>
            <Box pt={1} pb={1}>
              <Divider className={classes.horizantalDivder} />
            </Box>
            <Box>
              <DropoffLocationFilter
                onOpen={deliveryStateCodeOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'deliveryStateCode'}
              />
            </Box>
            <Box pt={1} pb={1}>
              <Divider className={classes.horizantalDivder} />
            </Box>
            <Box display='flex' justifyContent='space-between'>
              <PickupDatesFilter
                onOpen={pickupStartDateOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'pickupStartDate'}
              />
              <Box display='flex' justifyContent='center' flexDirection='column'>
                <Divider className={classes.verticalDivider} orientation='vertical' />
              </Box>
              <DropoffDatesFilter
                onOpen={deliveryStartDateOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'deliveryStartDate'}
              />
            </Box>
            <Box pt={3} pb={1}>
              {hasStartedSearch ? (
                <Button color='secondary' size='large' fullWidth onClick={clearFilterHandler}>
                  Clear All Filters
                </Button>
              ) : (
                <Button
                  color='primary'
                  size='large'
                  fullWidth
                  onClick={
                    pickupStateCode
                      ? startSearchHandler
                      : createToggleSelectedFilterHandler('pickupStateCode')
                  }>
                  Search
                </Button>
              )}
            </Box>
          </Box>
          <Box pt={2}>
            {hasStartedSearch && (
              <>
                <SingleSelect
                  className='mr4 mb4'
                  id='pickup-radius'
                  icon={<RadiusIcon size='large' />}
                  label='Pickup Radius'
                  options={RADIUS_OPTIONS}
                  onSelect={pickupRadiusChangeHandler}
                  selected={selectedPickupRadiusOption}
                  dataTest='pickup-radius'
                />
                {filters?.deliveryCity && filters?.deliveryStateCode && (
                  <SingleSelect
                    className='mr4 mb4'
                    id='dropoff-radius'
                    icon={<RadiusIcon size='large' />}
                    label='Dropoff Radius'
                    options={RADIUS_OPTIONS}
                    onSelect={deliveryRadiusChangeHandler}
                    selected={selectedDeliveryRadiusOption}
                    dataTest='dropoff-radius'
                  />
                )}
                <SingleSelect
                  className='mr4 mb4'
                  id='equipment-type'
                  icon={<DryVanIcon size='large' />}
                  label='Equipment'
                  options={EQUIPMENT_OPTIONS}
                  onSelect={equipmentChangeHandler}
                  selected={selectedEquipmentOption}
                  dataTest='equipment'
                />
              </>
            )}
          </Box>
          {showSavedLaneSwitch && (
            <div>
              <SavedLaneSwitch />
            </div>
          )}
        </>
      ) : (
        /**
         * Desktop view
         */
        <>
          <Box className={classes.root} data-intercom='fms-book-loads-search-bar'>
            <Box flexGrow={1}>
              <PickupLocationFilter
                onOpen={pickupStateCodeOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'pickupStateCode'}
              />
            </Box>
            <Box display='flex' alignItems='center' pl={1} pr={1}>
              <LaneArrowIcon size='large' />
            </Box>
            <Box flexGrow={1}>
              <DropoffLocationFilter
                onOpen={deliveryStateCodeOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'deliveryStateCode'}
              />
            </Box>
            <Box pl={1} pr={1}>
              <Divider orientation='vertical' className={classes.verticalDivider} />
            </Box>
            <Box flexGrow={1}>
              <PickupDatesFilter
                onOpen={pickupStartDateOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'pickupStartDate'}
              />
            </Box>
            <Box pl={1} pr={1}>
              <Divider orientation='vertical' className={classes.verticalDivider} />
            </Box>
            <Box flexGrow={1}>
              <DropoffDatesFilter
                onOpen={deliveryStartDateOpenHandler}
                onClose={closeHandler}
                value={params}
                onChange={changeParams}
                isSelected={selectedFilter === 'deliveryStartDate'}
              />
            </Box>
            <Box pl={1} display='flex' alignItems='center' pr={2}>
              {hasStartedSearch ? (
                <IconButton
                  Icon={CloseIcon}
                  onClick={clearFilterHandler}
                  color='tertiary'
                  size='large'
                />
              ) : (
                <Button
                  data-test='submit-load-search'
                  onClick={
                    pickupStateCode
                      ? startSearchHandler
                      : createToggleSelectedFilterHandler('pickupStateCode')
                  }
                  color='primary'
                  className={classes.searchButton}>
                  <SearchIcon size='large' />
                </Button>
              )}
            </Box>
          </Box>
          <Box pt={2}>
            {hasStartedSearch && (
              <Hidden smDown>
                <SingleSelect
                  className='mr4 mb4'
                  id='pickup-radius'
                  icon={<RadiusIcon size='large' />}
                  label='Pickup Radius'
                  options={RADIUS_OPTIONS}
                  onSelect={pickupRadiusChangeHandler}
                  selected={selectedPickupRadiusOption}
                  dataTest='pickup-radius'
                />
                {filters?.deliveryCity && filters?.deliveryStateCode && (
                  <SingleSelect
                    className='mr4 mb4'
                    id='dropoff-radius'
                    icon={<RadiusIcon size='large' />}
                    label='Dropoff Radius'
                    options={RADIUS_OPTIONS}
                    onSelect={deliveryRadiusChangeHandler}
                    selected={selectedDeliveryRadiusOption}
                    dataTest='dropoff-radius'
                  />
                )}
                <SingleSelect
                  className='mr4 mb4'
                  id='equipment-type'
                  icon={<DryVanIcon size='large' />}
                  label='Equipment'
                  options={EQUIPMENT_OPTIONS}
                  onSelect={equipmentChangeHandler}
                  selected={selectedEquipmentOption}
                  dataTest='equipment'
                />
              </Hidden>
            )}
          </Box>
          {showSavedLaneSwitch && (
            <div>
              <SavedLaneSwitch />
            </div>
          )}
        </>
      )}
    </Box>
  )
}
